'use client';

/* eslint-disable @next/next/no-img-element -- favicon/external/data-URI; next/image migration impractical for these dynamic logo/signature sources */
import { useState, useEffect, useCallback } from 'react';
import dynamic from 'next/dynamic';
import { Info } from 'lucide-react';
import type { Matter } from '@/lib/services/matters/types';
import type { Contact } from '@/lib/services/contacts/types';
import { getPmsStatus } from '@/lib/services/integrations/pms';
import type { PmsProvider } from '@/lib/services/integrations/pms';
import { getSmokeballConfiguration } from '@/lib/services/integrations/smokeball';
import { getActionStepConfiguration } from '@/lib/services/integrations/actionstep';
import {
  SmokeballSyncFields,
  type SmokeballSyncConfig
} from './smokeball-sync-fields';
import { ActionStepSyncFields } from './actionstep-sync-fields';

// Dynamically load PMS-specific sync panels
const SmokeballSyncPanel = dynamic(
  () =>
    import('./smokeball-sync-panel').then(
      (mod) => mod.MatterDetailSmokeballSyncPanel
    ),
  { ssr: false }
);

const ActionStepSyncPanel = dynamic(
  () =>
    import('./actionstep-sync-panel').then(
      (mod) => mod.MatterDetailActionStepSyncPanel
    ),
  { ssr: false }
);

/** Detail mode: existing matter with PMS provider */
interface PmsSyncPanelDetailProps {
  matter: Matter;
  contacts: Contact[];
  onSave?: (config: {
    stateOfMatter: string;
    matterTypeId: string;
    representativeOption?: string;
  }) => void;
  isSaving?: boolean;
  onNavigateToAddLpr?: () => void;
  /** Override to force-show the panel even when createMattersInPms is disabled */
  forceShow?: boolean;
}

/** Create mode: new matter, no Matter object yet */
interface PmsSyncPanelCreateProps {
  mode: 'create';
  matterTypeSlug: string;
  existingConfig?: {
    stateOfMatter?: string;
    matterTypeId?: string;
    representativeOption?: string;
  };
  onConfigChange: (config: SmokeballSyncConfig | null) => void;
  /** Whether the matter reference number has been set */
  hasReferenceNumber?: boolean;
}

export type PmsSyncPanelProps =
  | PmsSyncPanelDetailProps
  | PmsSyncPanelCreateProps;

function isCreateMode(
  props: PmsSyncPanelProps
): props is PmsSyncPanelCreateProps {
  return 'mode' in props && props.mode === 'create';
}

/**
 * PMS Sync Panel Router
 *
 * Detail mode: Dynamically loads the appropriate PMS sync panel based on
 * the matter's pmsProviderName.
 *
 * Create mode: Calls getPmsStatus() to detect the PMS provider,
 * then renders config fields directly (no save button — parent form handles submit).
 */
export function PmsSyncPanel(props: PmsSyncPanelProps) {
  if (isCreateMode(props)) {
    return <PmsSyncPanelCreate {...props} />;
  }

  return <PmsSyncPanelDetail {...props} />;
}

/** Detail mode — routes on matter.pmsProviderName */
function PmsSyncPanelDetail({
  matter,
  contacts,
  onSave,
  isSaving,
  onNavigateToAddLpr,
  forceShow
}: PmsSyncPanelDetailProps) {
  const { pmsProviderName } = matter;

  if (!pmsProviderName) {
    return null;
  }

  switch (pmsProviderName) {
    case 'smokeball':
      return (
        <SmokeballSyncPanel
          matter={matter}
          contacts={contacts}
          onSave={onSave}
          isSaving={isSaving}
          onNavigateToAddLpr={onNavigateToAddLpr}
          forceShow={forceShow}
        />
      );

    case 'actionstep':
      return (
        <ActionStepSyncPanel
          matter={matter}
          onSave={onSave}
          isSaving={isSaving}
          forceShow={forceShow}
        />
      );

    // Any other provider (including the unimplemented clio/leap) has no sync panel — degrade to
    // absent rather than to another provider's panel (LF-6342 audit, rule 4).
    default:
      return null;
  }
}

/** Create mode — detects PMS provider via getPmsStatus() and checks configuration */
function PmsSyncPanelCreate({
  matterTypeSlug,
  existingConfig,
  onConfigChange,
  hasReferenceNumber = false
}: PmsSyncPanelCreateProps) {
  const [provider, setProvider] = useState<PmsProvider | null>(null);
  const [isEnabled, setIsEnabled] = useState(false);
  const [createMattersInPms, setCreateMattersInPms] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchStatusAndConfig() {
      setIsLoading(true);

      const statusResult = await getPmsStatus();
      if (statusResult.success && statusResult.data) {
        setProvider(statusResult.data.provider);
        setIsEnabled(statusResult.data.isEnabled);

        // Only fetch config if the connected provider is enabled
        if (statusResult.data.isEnabled) {
          if (statusResult.data.provider === 'smokeball') {
            const configResult = await getSmokeballConfiguration();
            if (configResult.success && configResult.data) {
              // Empty/partial config has no matterSync — treat as not enabled (LF-5091).
              setCreateMattersInPms(
                configResult.data.matterSync?.createMattersInPms ?? false
              );
            }
          } else if (statusResult.data.provider === 'actionstep') {
            const configResult = await getActionStepConfiguration();
            if (configResult.success && configResult.data) {
              setCreateMattersInPms(
                configResult.data.matterSync?.createMattersInPms ?? false
              );
            }
          }
        }
      } else {
        setProvider(null);
        setIsEnabled(false);
      }

      setIsLoading(false);
    }

    fetchStatusAndConfig();
  }, []);

  // Track live config from the provider's sync-fields component for checklist display
  const [liveConfig, setLiveConfig] = useState<SmokeballSyncConfig | null>(
    null
  );
  const handleConfigChange = useCallback(
    (config: SmokeballSyncConfig | null) => {
      setLiveConfig(config);
      onConfigChange(config);
    },
    [onConfigChange]
  );

  // ActionStep's checklist must reflect each field independently (LF-6368) —
  // `liveConfig` above only resolves once both fields are complete (it gates
  // form submission), so it can't drive a per-field requirements checklist.
  const [actionStepFields, setActionStepFields] = useState<{
    stateOfMatter: string;
    matterTypeId: string;
  } | null>(null);
  const handleActionStepFieldsChange = useCallback(
    (fields: { stateOfMatter: string; matterTypeId: string }) => {
      setActionStepFields(fields);
    },
    []
  );

  // If already fully configured in edit mode, don't show panel
  const alreadyConfigured = !!existingConfig?.matterTypeId;

  // Notify parent when PMS is unavailable (after loading completes)
  useEffect(() => {
    if (!isLoading && (!provider || !isEnabled || !createMattersInPms)) {
      onConfigChange(null);
    }
  }, [isLoading, provider, isEnabled, createMattersInPms, onConfigChange]);

  if (isLoading) return null;
  if (!provider || !isEnabled || !createMattersInPms) return null;
  if (alreadyConfigured) return null;

  if (provider === 'smokeball') {
    return (
      <div className="bg-card rounded-lg border p-4">
        {/* Header with Smokeball logo */}
        <div className="flex items-start gap-3">
          <img
            src="/integrations/symbols/smokeball.png"
            alt="Smokeball"
            width={32}
            height={32}
            className="shrink-0 rounded-lg"
          />
          <div className="flex-1">
            <h3 className="text-foreground text-sm font-semibold">
              Smokeball Sync (Optional)
            </h3>
            <p className="text-muted-foreground mt-1 text-xs">
              Configure this matter to sync with Smokeball.
            </p>
          </div>
        </div>

        {/* Requirements checklist */}
        <div className="bg-info-subtle/50 mt-3 flex items-start gap-2 rounded-lg p-2.5">
          <Info className="text-info-subtle-foreground mt-0.5 h-3.5 w-3.5 shrink-0" />
          <div className="text-info-subtle-foreground pt-0.5 text-xs">
            <p>Requirements for Smokeball sync:</p>
            <ul className="mt-1.5 list-inside list-disc space-y-0.5">
              <li
                className={
                  hasReferenceNumber ? 'text-success-subtle-foreground' : ''
                }
              >
                Matter reference number set
                {hasReferenceNumber && ' \u2713'}
              </li>
              <li
                className={
                  liveConfig?.stateOfMatter
                    ? 'text-success-subtle-foreground'
                    : ''
                }
              >
                State of matter selected
                {liveConfig?.stateOfMatter && ' \u2713'}
              </li>
              <li
                className={
                  liveConfig?.matterTypeId
                    ? 'text-success-subtle-foreground'
                    : ''
                }
              >
                Smokeball matter type selected
                {liveConfig?.matterTypeId && ' \u2713'}
              </li>
            </ul>
          </div>
        </div>

        {/* Shared config fields */}
        <div className="mt-3">
          <SmokeballSyncFields
            matterTypeSlug={matterTypeSlug}
            initialConfig={existingConfig}
            onConfigChange={handleConfigChange}
            variant="create"
          />
        </div>
      </div>
    );
  }

  if (provider === 'actionstep') {
    return (
      <div className="bg-card rounded-lg border p-4">
        {/* Header with ActionStep logo */}
        <div className="flex items-start gap-3">
          <img
            src="/integrations/symbols/actionstep.png"
            alt="ActionStep"
            width={32}
            height={32}
            className="shrink-0 rounded-lg"
          />
          <div className="flex-1">
            <h3 className="text-foreground text-sm font-semibold">
              ActionStep Sync (Optional)
            </h3>
            <p className="text-muted-foreground mt-1 text-xs">
              Configure this matter to sync with ActionStep.
            </p>
          </div>
        </div>

        {/* Requirements checklist */}
        <div className="bg-info-subtle/50 mt-3 flex items-start gap-2 rounded-lg p-2.5">
          <Info className="text-info-subtle-foreground mt-0.5 h-3.5 w-3.5 shrink-0" />
          <div className="text-info-subtle-foreground pt-0.5 text-xs">
            <p>Requirements for ActionStep sync:</p>
            <ul className="mt-1.5 list-inside list-disc space-y-0.5">
              <li
                className={
                  hasReferenceNumber ? 'text-success-subtle-foreground' : ''
                }
              >
                Matter reference number set
                {hasReferenceNumber && ' \u2713'}
              </li>
              <li
                className={
                  actionStepFields?.stateOfMatter
                    ? 'text-success-subtle-foreground'
                    : ''
                }
              >
                State of matter selected
                {actionStepFields?.stateOfMatter && ' \u2713'}
              </li>
              <li
                className={
                  actionStepFields?.matterTypeId
                    ? 'text-success-subtle-foreground'
                    : ''
                }
              >
                ActionStep action type selected
                {actionStepFields?.matterTypeId && ' \u2713'}
              </li>
            </ul>
          </div>
        </div>

        {/* Shared config fields */}
        <div className="mt-3">
          <ActionStepSyncFields
            initialConfig={existingConfig}
            onConfigChange={handleConfigChange}
            onFieldsChange={handleActionStepFieldsChange}
            variant="create"
          />
        </div>
      </div>
    );
  }

  return null;
}
